import jStat from 'jstat';
import * as base from './base';

import { isInteger } from 'lodash';
import { CHART_TAIL_TYPES } from 'store/scenes/distributions/chart/enums';

const makePdFn = (trials, probability) => x =>
  jStat.binomial.pdf(x, trials, probability);

const makeCdfFn = (trials, probability) => x =>
  jStat.binomial.cdf(x, trials, probability);


const getPds = (numberOfTrials, probabilityOfSuccess) => {
  if (probabilityOfSuccess === 1) {
    return [{ x: numberOfTrials, y: 1 }];
  }
  const xMin = 0;
  const xMax = numberOfTrials + 1;

  const pdFn = makePdFn(numberOfTrials, probabilityOfSuccess);
  return base.getPds(xMin, xMax, pdFn, numberOfTrials + 1);
};

const getAlphaByCutoff = (numberOfTrials, probabilityOfSuccess, bLeftCutoff, oldBRightCutoff, newTailType) => {
  let bRightCutoff = oldBRightCutoff;

  if ((newTailType === 'both' || newTailType === 'center') && bLeftCutoff > oldBRightCutoff) {
    bRightCutoff = bLeftCutoff + 1;
  }

  const pdFn = makePdFn(numberOfTrials, probabilityOfSuccess);

  const leftAlfa = pdFn(bLeftCutoff);
  const rightAlfa = pdFn(bRightCutoff);

  return ({
    left: { x: bLeftCutoff, y: isFinite(leftAlfa) ? leftAlfa : 0 },
    right: { x: bRightCutoff, y: isFinite(rightAlfa) ? rightAlfa : 0 }
  });
};

const getCutoffsBySignTail = (preLeftCutoffValue, preRightCutoffValue, tailType, numberOfTrials) => {
  let leftCutoffValue = +preLeftCutoffValue;
  let rightCutoffValue = +preRightCutoffValue;

  // data is invalid
  if (preLeftCutoffValue === '') {
    leftCutoffValue = -1;
  }
  if (preRightCutoffValue === '') {
    rightCutoffValue = numberOfTrials + 1;
  }

  if (tailType === CHART_TAIL_TYPES.RIGHT) {
    rightCutoffValue = Math.ceil(leftCutoffValue) - 1;
  }

  if (tailType === CHART_TAIL_TYPES.CENTER) {
    if (leftCutoffValue <= rightCutoffValue) {
      leftCutoffValue = Math.ceil(leftCutoffValue) - 1;
      rightCutoffValue = Math.floor(rightCutoffValue);
    } else {
      rightCutoffValue = leftCutoffValue + 0.000001;
    }
  }

  if (tailType === CHART_TAIL_TYPES.BOTH) {
    if (leftCutoffValue < rightCutoffValue) {
      rightCutoffValue = Math.ceil(rightCutoffValue) - 1;
    } else {
      rightCutoffValue = Math.floor(leftCutoffValue);
    }
  }

  return {
    leftCutoffValue,
    rightCutoffValue
  };
};

const getAlphaByCutoffsAndTailType = (numberOfTrials, probabilityOfSuccess, preLeftCutoffValue, preRightCutoffValue, tailType) => {
  const { leftCutoffValue, rightCutoffValue } = getCutoffsBySignTail(preLeftCutoffValue, preRightCutoffValue, tailType, +numberOfTrials);

  // data is invalid
  if (numberOfTrials < 1 ||
    !isInteger(numberOfTrials) ||
    probabilityOfSuccess === '' ||
    probabilityOfSuccess < 0 ||
    probabilityOfSuccess > 1) {
    return -1;
  }

  let cdfFn = makeCdfFn(numberOfTrials, probabilityOfSuccess);

  if (tailType === CHART_TAIL_TYPES.RIGHT || tailType === CHART_TAIL_TYPES.BOTH) {
    // js is rounding 0.99 *10^-17 to 1
    if (cdfFn(rightCutoffValue) > 0 && cdfFn(rightCutoffValue) < 0.000001) {
      return 0.999999;
    }
  }

  if (tailType === CHART_TAIL_TYPES.LEFT) {
    // js is rounding 0.99 *10^-17 to 1
    if (cdfFn(leftCutoffValue) > 0 && cdfFn(leftCutoffValue) < 0.000001) {
      return 0.000001;
    }
  }

  if (tailType === CHART_TAIL_TYPES.CENTER) {
    // js is rounding 0.01 *10^-17 to 0
    const diff = Math.abs(cdfFn(leftCutoffValue) - cdfFn(rightCutoffValue));
    if (diff > 0 && diff < 0.000001) {
      return 0.000001;
    }
  }

  if (tailType === CHART_TAIL_TYPES.EQUAL) {
    // data is invalid
    if (
      leftCutoffValue < 0 ||
      preLeftCutoffValue === '' ||
      !isInteger(+leftCutoffValue) ||
      leftCutoffValue > numberOfTrials
    ) {
      return 0;
    }
    cdfFn = makePdFn(numberOfTrials, probabilityOfSuccess);
  }

  const alpha = base.getAlphaByCutoffsAndTailType(cdfFn, +leftCutoffValue, +rightCutoffValue, tailType);

  return alpha;
};

const getCutoffsByCutoffXValues = (numberOfTrials, probabilityOfSuccess, leftCutoffValue, RightCutoffValue) => {
  const pdFn = makePdFn(numberOfTrials, probabilityOfSuccess);

  return base.getCutoffsByCutoffXValues(pdFn, leftCutoffValue, RightCutoffValue);
};


const binomialDistributionFunctions = {
  getPds: ({ numberOfTrials, probabilityOfSuccess }) => getPds(+numberOfTrials, +probabilityOfSuccess),

  getCutoffsByAlpha: ({ numberOfTrials, probabilityOfSuccess, bLeftCutoff, bRightCutoff }, _, newTailType) => {
    return getAlphaByCutoff(+numberOfTrials, +probabilityOfSuccess, +bLeftCutoff, +bRightCutoff, newTailType);
  },

  getAlphaByCutoff: ({ numberOfTrials, probabilityOfSuccess }, bLeftCutoff, bRightCutoff, newTailType) => {
    return getAlphaByCutoff(+numberOfTrials, +probabilityOfSuccess, +bLeftCutoff, +bRightCutoff, newTailType);
  },

  getCutoffsBySignTail: (preLeftCutoffValue, preLightCutoffValue, tailType) => (
    getCutoffsBySignTail(preLeftCutoffValue, preLightCutoffValue, tailType)
  ),

  getAlphaByCutoffsAndTailType: ({ numberOfTrials, probabilityOfSuccess }, leftCutoffValue, rightCutoffValue, tailType) => (
    getAlphaByCutoffsAndTailType(+numberOfTrials, probabilityOfSuccess, leftCutoffValue, rightCutoffValue, tailType)
  ),

  getCutoffsByCutoffXValues: ({ numberOfTrials, probabilityOfSuccess }, leftCutoffValue, rightCutoffValue) => (
    getCutoffsByCutoffXValues(+numberOfTrials, probabilityOfSuccess, leftCutoffValue, rightCutoffValue)
  )
};

export default binomialDistributionFunctions;
